import 'client-only'

import { IS_PROD_ENV, IS_PROD_BUILD, CONFIG_DISABLE_ATTENTIVE } from '@src/config/env'
import { initializeExperimentManager } from '@src/experiments'
import { statsigExperimentManager } from '@src/experiments/statsig-integration'
import { hydrateLocaleConfigFromNextData } from '@src/modules/global-state/locale-config.browser'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import attachMessageListener from '@src/quince-site-common/src/helpers/window-message-listener'
import initializeAnalytics from '@src/quince-site-common/src/trackers/initializeAnalytics'
import addAttentiveSdk from '@src/quince-site-common/src/utils/addAttentiveSDK'
import shouldDisableTelemetry from '@src/quince-site-common/src/utils/check-qsf-telemetry'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import {
  executeSafely,
  extractQueryParamFromPageLocation,
  getUserSession,
  logoutUser,
  getCookie,
  saveState,
} from '@src/quince-site-integrated/src/utils'
import { UserAgnosticGlobalFeatureGates } from '@src/server-side/api/feature-gates.types'
import { shouldEnableI18nClient } from '@src/server-side/utils/i18n-preview.utils'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import TrustArcCl from '@src/telemetry/integrations/trustarc'
import { BLOCK_TELEMETRY_COOKIE_NAME, BLOCK_TELEMETRY_COOKIE_VALUE } from '@src/telemetry/telemetry-blockers.constants'
import { isAdvertisingConsentRequired, isCountryCompliantLocale } from '@src/telemetry/telemetry-registry'
import { getStoreProgramIdFromStorage } from '@src/utils/b2b/b2b.utils'
import { initializeConsoleMonkeyPatch } from '@src/utils/console-monkey-patch'
import { getContentVersionOnClient } from '@src/utils/general.utils'

declare global {
  interface Window {
    __debug: Record<string, unknown>
  }
}

function deleteTokensFromLocalStorage() {
  // eslint-disable-next-line @quince/no-direct-localstorage
  window.localStorage.removeItem('jwt_token')
}

const unregisterBlockedServiceWorkers = async () => {
  // eslint-disable-next-line i18next/no-literal-string
  const blockedWorkers = ['sw_v2.js']
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    for (const registration of registrations) {
      for (const worker of blockedWorkers) {
        if (registration.active?.scriptURL.endsWith(worker)) await registration.unregister()
      }
    }
  }
}

const saveFirstPurchaseCouponCode = saveState('firstPurchaseCouponCode')

function postInitialization() {
  console.log('CONFIG_DISABLE_ATTENTIVE :>> ', CONFIG_DISABLE_ATTENTIVE)
  const isB2BFlow = !!getStoreProgramIdFromStorage()

  attachMessageListener()
  // Skip Attentive SDK load entirely for countryCompliant locales (e.g. UK) until
  // the user explicitly grants advertising consent via TrustArc. On consent the
  // page reloads (attachTrustArcCloseReloadListener) so Attentive will init then.
  if (!CONFIG_DISABLE_ATTENTIVE && !isB2BFlow && !isAdvertisingConsentRequired()) {
    const { initializeAttentive } = addAttentiveSdk()
    initializeAttentive()
  }
  const discountCode = extractQueryParamFromPageLocation('discount_code')
  const isBase64Encoded = extractQueryParamFromPageLocation('is_enc')
  if (discountCode)
    saveFirstPurchaseCouponCode({
      code: isBase64Encoded ? atob(discountCode).toString() : discountCode,
    })

  unregisterBlockedServiceWorkers()
    .then(() => {
      // do nothing fire and forget
    })
    .catch((e: unknown) => {
      console.error('Error while unregistering blocked service workers', e)
    })
}

let isInitialized = false
const initializeClientEntryFunctions = () => {
  if (isInitialized || !isBrowser() || shouldDisableTelemetry()) {
    return
  }
  const blockTrackingCookie = getCookie(BLOCK_TELEMETRY_COOKIE_NAME) ?? ''
  const isPixelBlocked = blockTrackingCookie === BLOCK_TELEMETRY_COOKIE_VALUE.BLOCK_PIXEL
  const isIABlocked = blockTrackingCookie === BLOCK_TELEMETRY_COOKIE_VALUE.BLOCK_IA

  // Register consent-script injector before TrustArc init so the listener is in
  // place before any consent event can fire. Lazy-loaded via dynamic import so the
  // injector module stays out of the eager `_app` bundle — it's only exercised after
  // a user grants consent. The import resolves within a microtask, far before TrustArc
  // finishes loading and a consent event could fire, so listener timing is preserved.
  executeSafely(() => {
    void import(/* webpackChunkName: "consent-scripts" */ '@src/telemetry/inject-consent-scripts')
      .then(({ registerConsentScriptInjector }) => {
        registerConsentScriptInjector()
      })
      .catch(() => {
        /* injector chunk failed to load — non-fatal; consent scripts simply won't inject */
      })
  })

  // For UK (SDK disabled), generate an initial _mp_user_device_id so $device_id
  // is present in ALL SGTM events — pre- AND post-consent. $device_id is
  // pseudonymous and not consent-gated, so this is keyed off the locale only
  // (isCountryCompliantLocale), NOT advertising consent. On consent grant,
  // rotateDeviceId() replaces it with a fresh UUID to unlink pre/post-consent.
  // Consent gating lives solely on $user_id (see mixpanel.helper.getCommonProperties).
  executeSafely(() => {
    if (isCountryCompliantLocale()) {
      mixpanelCl.initDeviceId()
    }
  })

  // Initialize TrustArc
  executeSafely(() => {
    TrustArcCl.init().catch((error: unknown) => {
      console.error('Error initializing TrustArc', error)
    })
  })

  // if (IS_PROD_ENV && IS_PROD_BUILD && !isPixelBlocked) {
  //   executeSafely(() => {
  //     initializeTelemetry()
  //   })
  // }

  // Initialize analytics
  executeSafely(() => {
    if (!isIABlocked)
      initializeAnalytics({
        enableTelemetry: IS_PROD_ENV && IS_PROD_BUILD && !isPixelBlocked,
      })
  })

  deleteTokensFromLocalStorage()
  executeSafely(() => {
    postInitialization()
  })
  isInitialized = true
}

const fetchInitialUserSessionForNativeView = () => {
  if (getIsNativeViewClient()) {
    getUserSession()
      .then(session => session)
      .catch((err: unknown) => {
        console.error('Error getting user session', err)
      })
  }
}

function initWindow() {
  if (!isBrowser()) return
  window.__debug = {
    release_build: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    release_commit: process.env.NEXT_PUBLIC_RELEASE_COMMIT,
    isI18nEnabled: shouldEnableI18nClient(),
    previewDetails: {
      contentVersion: getContentVersionOnClient(),
    },
  }
  window.getUserSession = getUserSession
  window.logoutUser = logoutUser
  fetchInitialUserSessionForNativeView()
}

/**
 * This is intentional. You want auth to initialize before anything else
 * Reference - https://docs.amplify.aws/gen1/react/start/getting-started/setup/#set-up-frontend
 *
 * This function is intentionally added to run JS before any of
 * react components are mounted and effects are run.
 *
 * Please add only critical items here and get any changes reviewed by leads
 */
;(function initializeAtJSParse() {
  const isStatsigBlocked = (getCookie(BLOCK_TELEMETRY_COOKIE_NAME) ?? '') === BLOCK_TELEMETRY_COOKIE_VALUE.BLOCK_STATSIG

  // Hydrate locale config from __NEXT_DATA__ for non-React code (trustArcDomain etc.)
  if (isBrowser()) {
    executeSafely(() => {
      hydrateLocaleConfigFromNextData()
    })
  }

  // Initialize console monkey patch first (before any other logging)
  if (isBrowser()) {
    executeSafely(() => {
      initializeConsoleMonkeyPatch(IS_PROD_ENV && IS_PROD_BUILD)
    })
  }

  if (isBrowser()) {
    // Initialize experiment manager
    executeSafely(() => {
      // Skip the legacy hash-based experiment bucketing for UK (countryCompliant)
      // users who have not granted advertising consent. The manager uses the real
      // lbUserId as the bucketing key, which is a persistent identifier that must
      // not be processed before consent in GDPR-covered locales.
      // Statsig (below) still runs but with UNTRACKED_UK_USER stableID instead.
      if (!isAdvertisingConsentRequired()) {
        initializeExperimentManager()
      }
      // statsigExperimentManager.initialize() only reads existing Statsig bucket
      // assignments from localStorage — no user IDs involved, safe to run always.
      if (!isStatsigBlocked) statsigExperimentManager.initialize()
    })
  }
})()

export function initializeClientEntry(_userAgnosticGlobalFeatureGates: UserAgnosticGlobalFeatureGates) {
  initializeClientEntryFunctions()
  initWindow()
}
